import React, { useState, useEffect } from 'react';
import { Clock, Play, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import TaskList from '../dashboard/TaskList';
import { apiGet, apiPost, apiPut } from '../../utils/api';
import { toast } from 'react-hot-toast';
import { Card, Button, Spinner } from '../design-system';

const PatientTasks = ({ onStartTask, onStatusUpdate }) => {
  const { userInfo, user, isDemoMode } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await apiGet('/.netlify/functions/get-my-tasks');
      setTasks(response.tasks || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  // Task action handlers - Source of authority for task actions
  const handleStartTask = async (taskId) => {
    try {
      console.log('PatientTasks handleStartTask called with taskId:', taskId);
      
      const response = await apiPost('/.netlify/functions/start-task-session', { taskId });

      // Don't show toast here - let parent handle user feedback
      // toast.success('Task started successfully!');

      // Notify parent component to handle navigation
      if (onStartTask) {
        onStartTask(taskId);
      }
    } catch (error) {
      console.error('Error starting task:', error);
      toast.error(error.message || 'Failed to start task');
    }
  };

  const handleStatusUpdate = async (taskId, status) => {
    try {
      const response = await apiPut('/.netlify/functions/update-task-status', { taskId, status });

      const statusMessages = {
        'started': 'Task started!',
        'completed': 'Task marked as complete!',
        'pending': 'Task marked as pending'
      };
      toast.success(statusMessages[status] || 'Task status updated!');

      // Refresh tasks to show updated status
      loadTasks();
      
      // Notify parent component if needed
      if (onStatusUpdate) {
        onStatusUpdate(taskId, status);
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error(error.message || 'Failed to update task status');
    }
  };

  const handleNavigateToTask = (taskId, session) => {
    // Use our own handleStartTask function - source of authority
    handleStartTask(taskId);
  };

  const filteredTasks = tasks.filter(task => {
    switch (activeFilter) {
      case 'pending':
        return task.status === 'pending';
      case 'started':
        return task.status === 'started';
      case 'completed':
        return task.status === 'completed';
      case 'overdue':
        return task.status === 'pending' && task.dueDate && new Date(task.dueDate) < new Date();
      default:
        return true;
    }
  });

  const getTaskStats = () => {
    const total = tasks.length;
    const pending = tasks.filter(task => task.status === 'pending').length;
    const started = tasks.filter(task => task.status === 'started').length;
    const completed = tasks.filter(task => task.status === 'completed').length;
    const overdue = tasks.filter(task => 
      task.status === 'pending' && task.dueDate && new Date(task.dueDate) < new Date()
    ).length;

    return { total, pending, started, completed, overdue };
  };

  const stats = getTaskStats();

  // Remove the local TaskPage rendering - let parent handle it
  // if (currentTaskId) {
  //   return (
  //     <TaskPage 
  //       taskId={currentTaskId} 
  //       onBack={handleBackToTasks}
  //     />
  //   );
  // }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 h-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6 space-y-3 sm:space-y-0">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-gray-900">My Tasks</h2>
          <p className="text-sm md:text-base text-gray-600 mt-1">Manage your assigned tasks and track your progress</p>
        </div>
      </div>


      {/* Filter Tabs */}
      <div className="flex space-x-1 mb-4 md:mb-6 bg-gray-100 p-1 rounded-lg overflow-x-auto">
        <button
          onClick={() => setActiveFilter('all')}
          className={`flex-shrink-0 py-2 px-3 text-sm font-medium rounded-md transition-colors duration-200 ${
            activeFilter === 'all'
              ? 'bg-white text-indigo-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          All ({stats.total})
        </button>
        <button
          onClick={() => setActiveFilter('pending')}
          className={`flex-shrink-0 py-2 px-3 text-sm font-medium rounded-md transition-colors duration-200 ${
            activeFilter === 'pending'
              ? 'bg-white text-indigo-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Pending ({stats.pending})
        </button>
        <button
          onClick={() => setActiveFilter('started')}
          className={`flex-shrink-0 py-2 px-3 text-sm font-medium rounded-md transition-colors duration-200 ${
            activeFilter === 'started'
              ? 'bg-white text-indigo-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Started ({stats.started})
        </button>
        <button
          onClick={() => setActiveFilter('completed')}
          className={`flex-shrink-0 py-2 px-3 text-sm font-medium rounded-md transition-colors duration-200 ${
            activeFilter === 'completed'
              ? 'bg-white text-indigo-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Completed ({stats.completed})
        </button>
        <button
          onClick={() => setActiveFilter('overdue')}
          className={`flex-shrink-0 py-2 px-3 text-sm font-medium rounded-md transition-colors duration-200 ${
            activeFilter === 'overdue'
              ? 'bg-white text-indigo-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Overdue ({stats.overdue})
        </button>
      </div>

      {/* Task List */}
      {loading ? (
        <div className="text-center py-8">
          <Spinner size="large" />
          <p className="text-gray-600 mt-2">Loading tasks...</p>
        </div>
      ) : (
        <TaskList 
          tasks={filteredTasks} 
          onTaskUpdate={loadTasks}
          isTherapist={false}
          onNavigateToTask={handleNavigateToTask}
          onStartTask={handleStartTask}
          onStatusUpdate={handleStatusUpdate}
          disableApiCalls={true}
        />
      )}
    </div>
  );
};

export default PatientTasks; 